import { useEffect } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { useAuth } from '../../hooks/useAuth';
import { UserMenu } from '../../components/UserMenu';
import { MilesLogo } from '../../components/MilesLogo';
import {
  User,
  CreditCard,
  Key,
  Layers,
  Share2,
  Bot,
  BarChart3,
  Users,
  Globe,
} from 'lucide-react';
import { PulsingDot } from '../../components/ui/pulsing-dot';
import { cn } from '../../lib/utils';
import { useReferralPartner } from '../../hooks/useReferral';

type SettingsTab =
  | 'account'
  | 'team'
  | 'sites'
  | 'plans'
  | 'usage'
  | 'billing'
  | 'api-keys'
  | 'agents'
  | 'referrals';

interface NavItem {
  tab: SettingsTab;
  label: string;
  path: string;
  icon: typeof User;
}

type NavSection = NavItem[];

const BASE_NAV_SECTIONS: NavSection[] = [
  [
    { tab: 'account', label: 'Account', path: '/settings/account', icon: User },
    { tab: 'team', label: 'Agency', path: '/settings/team', icon: Users },
    { tab: 'sites', label: 'Sites', path: '/settings/sites', icon: Globe },
  ],
  [
    { tab: 'agents', label: 'Agents', path: '/settings/agents', icon: Bot },
    {
      tab: 'api-keys',
      label: 'API Keys',
      path: '/settings/api-keys',
      icon: Key,
    },
  ],
  [
    {
      tab: 'plans',
      label: 'Subscription',
      path: '/settings/plans',
      icon: Layers,
    },
    { tab: 'usage', label: 'Usage', path: '/settings/usage', icon: BarChart3 },
    {
      tab: 'billing',
      label: 'Billing',
      path: '/settings/billing',
      icon: CreditCard,
    },
  ],
];

interface SettingsLayoutProps {
  activeTab: SettingsTab;
  children: React.ReactNode;
}

export function SettingsLayout({ activeTab, children }: SettingsLayoutProps) {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const { data: partnerData } = useReferralPartner();

  // Build nav sections — conditionally include Referrals in the billing group
  const navSections: NavSection[] = BASE_NAV_SECTIONS.map((section) => {
    if (
      Array.isArray(section) &&
      section.some((item) => item.tab === 'billing') &&
      partnerData?.isPartner
    ) {
      return [
        ...section,
        {
          tab: 'referrals' as SettingsTab,
          label: 'Referrals',
          path: '/settings/referrals',
          icon: Share2,
        },
      ];
    }
    return section;
  });

  // Flat list for breadcrumb label lookup
  const allItems = navSections.flatMap((s) => (Array.isArray(s) ? s : []));

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login' });
    }
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading) {
    return (
      <div className="h-dvh flex flex-col px-4 pb-4 pt-2 gap-2">
        <div className="h-12 shrink-0" />
        <div className="miles-container flex-1 min-h-0 flex items-center justify-center">
          <PulsingDot />
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col px-4 pb-4 pt-2 gap-2">
      {/* Header */}
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 text-sm">
          <MilesLogo />
          <span className="text-white/30">/</span>
          <Link
            to="/settings/account"
            className="text-white/60 hover:text-white transition-colors font-medium"
          >
            Settings
          </Link>
          <span className="text-white/30">/</span>
          <span className="text-white font-medium">
            {allItems.find((item) => item.tab === activeTab)?.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {user && <UserMenu user={user} />}
        </div>
      </div>

      {/* Content area with sidebar */}
      <div className="miles-container flex-1 min-h-0 flex flex-col lg:flex-row">
        {/* Sidebar */}
        <nav className="flex w-full shrink-0 gap-1 overflow-x-auto border-b border-white/10 p-2 scrollbar-thin lg:w-48 lg:flex-col lg:overflow-visible lg:border-b-0 lg:border-r lg:p-3">
          {navSections.map((section, sectionIdx) => {
            if (!Array.isArray(section)) return null;
            return (
              <div
                key={sectionIdx}
                className="flex shrink-0 gap-1 lg:block lg:shrink"
              >
                {sectionIdx > 0 && (
                  <div className="mx-1 h-8 border-l border-white/10 lg:mx-0 lg:my-2 lg:h-0 lg:border-l-0 lg:border-t" />
                )}
                {section.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.tab;
                  return (
                    <Link
                      key={item.tab}
                      to={item.path}
                      className={cn(
                        'flex items-center gap-2.5 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors',
                        isActive
                          ? 'bg-white/10 text-white font-medium'
                          : 'text-white/60 hover:text-white hover:bg-white/5',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Main content */}
        <div className="flex-1 min-w-0 overflow-auto p-4 scrollbar-thin sm:p-6">
          <div className="w-full">{children}</div>
        </div>
      </div>
    </div>
  );
}
